import { useMutation } from '@apollo/client';

import {
  GenerateDetailedDataReportFileUrlMutation,
  GenerateStandardDeliveryDeviceHigherCategoryReportFileUrlMutation,
  GenerateStandardDeliveryReportFileUrlMutation,
  MutationGenerateDetailedDataReportFileUrlArgs,
  MutationGenerateStandardDeliveryDeviceHigherCategoryReportFileUrlArgs,
  MutationGenerateStandardDeliveryReportFileUrlArgs,
  ReportSortOptions,
} from '../types';
import { GENERATE_DETAILED_DATA_REPORT_FILE_URL } from './generateDetailedDataReportFileUrl';
import { GENERATE_STANDARD_DELIVERY_DEVICE_HIGHER_CATEGORY_REPORT_FILE_URL } from './generateStandardDeliveryDeviceHigherCategoryReportFileUrl';
import { GENERATE_STANDARD_DELIVERY_REPORT_FILE_URL } from './generateStandardDeliveryReportFileUrl';
import {
  ExportReportOptions,
  isDetailedDataReportExportFilters,
  isStandardDeliveryDeviceHigherCategoryReportExportFilters,
  isStandardDeliveryReportExportFilters,
  ReportForExport,
} from './types';

type UseGenerateReportFileUrl = {
  generate: (reportData: ReportForExport, options?: ReportSortOptions) => Promise<string | undefined>;
  loading: boolean;
};

export const useGenerateReportFileUrl = (): UseGenerateReportFileUrl => {
  const [generateStandardDeliveryReportFileUrl, { loading: standardDeliveryLoading }] = useMutation<
    GenerateStandardDeliveryReportFileUrlMutation,
    MutationGenerateStandardDeliveryReportFileUrlArgs
  >(GENERATE_STANDARD_DELIVERY_REPORT_FILE_URL);

  const [generateDetailedDataReportFileUrl, { loading: detailedDataLoading }] = useMutation<
    GenerateDetailedDataReportFileUrlMutation,
    MutationGenerateDetailedDataReportFileUrlArgs
  >(GENERATE_DETAILED_DATA_REPORT_FILE_URL);

  const [
    generateStandardDeliveryDeviceHigherCategoryReportFileUrl,
    { loading: standardDeliveryDeviceHigherCategoryLoading },
  ] = useMutation<
    GenerateStandardDeliveryDeviceHigherCategoryReportFileUrlMutation,
    MutationGenerateStandardDeliveryDeviceHigherCategoryReportFileUrlArgs
  >(GENERATE_STANDARD_DELIVERY_DEVICE_HIGHER_CATEGORY_REPORT_FILE_URL);

  const handleGenerateReportFileUrl = async (
    reportData: ReportForExport,
    options?: ExportReportOptions
  ): Promise<string | undefined> => {
    const reportCommonFields = {
      templateId: reportData.templateId,
      dateRange: reportData.dateRange,
      endDate: reportData.endDate,
      exportFormat: reportData.exportFormat,
      startDate: reportData.startDate,
    };

    if (isStandardDeliveryReportExportFilters(reportData.filters)) {
      const { data } = await generateStandardDeliveryReportFileUrl({
        variables: {
          reportData: {
            ...reportCommonFields,
            filters: {
              adProduct: reportData.filters.adProduct,
              campaignIds: reportData.filters.campaignIds,
              cpm: reportData.filters.cpm,
              endDate: reportData.filters.endDate,
              orderGoal: reportData.filters.orderGoal,
              organizationId: reportData.filters.organizationId,
              revenue: reportData.filters.revenue,
              startDate: reportData.filters.startDate,
            },
          },
          sortOptions: {
            sortBy: options?.sortBy,
            sortOrder: options?.sortOrder,
          },
          searchTerm: options?.searchTerm,
        },
      });

      return data?.generateStandardDeliveryReportFileUrl;
    }

    if (isDetailedDataReportExportFilters(reportData.filters)) {
      const { data } = await generateDetailedDataReportFileUrl({
        variables: {
          reportData: {
            ...reportCommonFields,
            filters: {
              advertiserId: reportData.filters.advertiserId,
              lineItems: reportData.filters.lineItems,
              metricsTypes: reportData.filters.metricsTypes,
              rollUpType: reportData.filters.rollUpType,
            },
          },
          sortOptions: {
            sortBy: options?.sortBy,
            sortOrder: options?.sortOrder,
          },
          searchTerm: options?.searchTerm,
        },
      });

      return data?.generateDetailedDataReportFileUrl;
    }

    if (isStandardDeliveryDeviceHigherCategoryReportExportFilters(reportData.filters)) {
      const { data } = await generateStandardDeliveryDeviceHigherCategoryReportFileUrl({
        variables: {
          reportData: {
            ...reportCommonFields,
            filters: {
              advertiserId: reportData.filters.advertiserId,
              adProduct: reportData.filters.adProduct,
              campaignIds: reportData.filters.campaignIds,
              cpm: reportData.filters.cpm,
              deliveryGoal: reportData.filters.deliveryGoal,
              endDate: reportData.filters.endDate,
              organizationId: reportData.filters.organizationId,
              lifetimeCompletions: reportData.filters.lifetimeCompletions,
              revenue: reportData.filters.revenue,
              startDate: reportData.filters.startDate,
            },
          },
          sortOptions: {
            sortBy: options?.sortBy,
            sortOrder: options?.sortOrder,
          },
          searchTerm: options?.searchTerm,
        },
      });

      return data?.generateStandardDeliveryDeviceHigherCategoryReportFileUrl;
    }

    return undefined;
  };

  return {
    generate: handleGenerateReportFileUrl,
    loading: standardDeliveryLoading || detailedDataLoading || standardDeliveryDeviceHigherCategoryLoading,
  };
};
